import * as THREE from 'three';

/** Dense seating + phone flashes — matches penalty stadium quality. */
export function createTunnelDeckTexture(brightness: number, seed = 1) {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const ctx = c.getContext('2d')!;
  const b = THREE.MathUtils.clamp(brightness, 0.15, 1);
  ctx.fillStyle = `rgb(${Math.floor(12 * b)},${Math.floor(15 * b)},${Math.floor(22 * b)})`;
  ctx.fillRect(0, 0, c.width, c.height);

  let rng = seed * 99991;
  const rnd = () => {
    rng = (rng * 16807) % 2147483647;
    return (rng - 1) / 2147483646;
  };

  const rows = 10;
  const rowH = (c.height - 20) / rows;
  for (let row = 0; row < rows; row++) {
    const y = 10 + row * rowH;
    const depth = 0.55 + (row / rows) * 0.45;
    const seatShade = (row % 2 === 0 ? 0.55 : 0.4) * depth;
    ctx.fillStyle = `rgba(${Math.floor(32 * b)},${Math.floor(38 * b)},${Math.floor(50 * b)},${seatShade})`;
    ctx.fillRect(0, y, c.width, rowH - 1.2);
    ctx.fillStyle = `rgba(${Math.floor(16 * b)},${Math.floor(20 * b)},${Math.floor(28 * b)},0.92)`;
    ctx.fillRect(0, y + rowH - 2.2, c.width, 1.5);

    for (let x = 4; x < c.width - 4; ) {
      const block = Math.floor(x / 56);
      if (block % 9 === 0) {
        ctx.fillStyle = `rgba(${Math.floor(8 * b)},${Math.floor(10 * b)},${Math.floor(14 * b)},0.97)`;
        ctx.fillRect(x, y, 9, rowH - 1.5);
        ctx.fillStyle = `rgba(90,100,120,${0.25 * b})`;
        ctx.fillRect(x + 3, y + 2, 2, rowH - 4);
        x += 12;
        continue;
      }

      const roll = rnd();
      if (roll < 0.14) {
        x += 2.4 + rnd() * 1.8;
        continue;
      }

      let br: number;
      let bg: number;
      let bb: number;
      let a: number;
      if (roll > 0.96) {
        br = 255;
        bg = 252;
        bb = 245;
        a = (0.7 + rnd() * 0.3) * b;
      } else if (roll > 0.88) {
        br = 40;
        bg = 200;
        bb = 255;
        a = (0.45 + rnd() * 0.4) * b;
      } else if (roll > 0.78) {
        br = 210;
        bg = 240;
        bb = 70;
        a = (0.35 + rnd() * 0.35) * b;
      } else if (roll > 0.62) {
        br = Math.floor(30 + rnd() * 50);
        bg = Math.floor(45 + rnd() * 55);
        bb = Math.floor(90 + rnd() * 90);
        a = 0.85 * b;
      } else if (roll > 0.48) {
        br = Math.floor(100 + rnd() * 50);
        bg = Math.floor(70 + rnd() * 35);
        bb = Math.floor(50 + rnd() * 30);
        a = 0.82 * b;
      } else {
        br = Math.floor(45 + rnd() * 55);
        bg = Math.floor(50 + rnd() * 50);
        bb = Math.floor(60 + rnd() * 55);
        a = 0.78 * b;
      }

      const bw = 2.0 + rnd() * 1.5;
      const bh = rowH * (0.48 + rnd() * 0.32);
      const bx = x + rnd() * 0.5;
      const by = y + rowH - bh - 1.8;
      ctx.fillStyle = `rgba(${br},${bg},${bb},${a})`;
      ctx.beginPath();
      ctx.ellipse(bx + bw * 0.5, by + bh * 0.62, bw * 0.52, bh * 0.4, 0, 0, Math.PI * 2);
      ctx.fill();
      const hr = 0.75 + rnd() * 0.65;
      ctx.beginPath();
      ctx.ellipse(bx + bw * 0.5, by + bh * 0.2, hr, hr * 1.05, 0, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${Math.min(255, br + 25)},${Math.min(255, bg + 18)},${Math.min(255, bb + 12)},${a})`;
      ctx.fill();

      if (roll > 0.91) {
        ctx.fillStyle = `rgba(255,255,255,${0.55 * b})`;
        ctx.fillRect(bx + bw * 0.7, by + bh * 0.45, 1.2, 1.8);
      }

      x += 2.6 + rnd() * 1.9;
    }
  }

  const vig = ctx.createLinearGradient(0, 0, c.width, 0);
  vig.addColorStop(0, 'rgba(0,0,0,0.32)');
  vig.addColorStop(0.1, 'rgba(0,0,0,0)');
  vig.addColorStop(0.9, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.32)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, c.width, c.height);

  const haze = ctx.createLinearGradient(0, 0, 0, c.height);
  haze.addColorStop(0, 'rgba(10,18,32,0.35)');
  haze.addColorStop(0.55, 'rgba(10,18,32,0)');
  haze.addColorStop(1, 'rgba(0,0,0,0.12)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, 0, c.width, c.height);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 8;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

export function createTunnelSkyTexture() {
  const c = document.createElement('canvas');
  c.width = 1536;
  c.height = 768;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 768);
  g.addColorStop(0, '#02040a');
  g.addColorStop(0.35, '#07101c');
  g.addColorStop(0.62, '#0c1a30');
  g.addColorStop(0.82, '#12243c');
  g.addColorStop(1, '#1a3048');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 1536, 768);

  for (let i = 0; i < 220; i++) {
    const x = Math.random() * 1536;
    const y = Math.random() * 420;
    const a = 0.25 + Math.random() * 0.7;
    const r = Math.random() < 0.08 ? 1.4 : 0.6;
    ctx.fillStyle = `rgba(230,240,255,${a})`;
    ctx.fillRect(x, y, r, r);
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Grass with horizontal mowing stripes and a center circle — matches the exit plate. */
export function createTunnelPitchTexture() {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 1024;
  const ctx = c.getContext('2d')!;

  for (let i = 0; i < 28; i++) {
    ctx.fillStyle = i % 2 === 0 ? '#2db34f' : '#24943f';
    ctx.fillRect(0, (i / 28) * 1024, 1024, 1024 / 28 + 2);
  }

  for (let i = 0; i < 12000; i++) {
    const x = Math.random() * 1024;
    const y = Math.random() * 1024;
    const g = 28 + Math.random() * 28;
    ctx.fillStyle = `rgba(${g},${g + 48},${g + 8},${0.05 + Math.random() * 0.07})`;
    ctx.fillRect(x, y, 1.2, 1.2);
  }

  ctx.strokeStyle = 'rgba(245,250,255,0.72)';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(512, 470, 118, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(512, 470, 8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(245,250,255,0.78)';
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(0, 470);
  ctx.lineTo(1024, 470);
  ctx.stroke();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 8;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

export function createTunnelLedRibbonTexture() {
  const c = document.createElement('canvas');
  c.width = 2048;
  c.height = 96;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 0, 96);
  g.addColorStop(0, '#f7f4ea');
  g.addColorStop(0.45, '#efe6d2');
  g.addColorStop(1, '#e4d8be');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2048, 96);

  ctx.fillStyle = '#d8c49a';
  ctx.fillRect(0, 0, 2048, 5);
  ctx.fillRect(0, 91, 2048, 5);

  ctx.font = '800 40px "Barlow Condensed", Impact, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#1a1c20';
  ctx.shadowColor = 'transparent';
  const copy =
    '  AI  -  CLOUD  -  FRONTEND  -  ML  -  THREE  -  BACKEND  -  GENERATIVE AI  -  LLM  -  MACHINE LEARNING  -  ALKAY\'S LEARNING  -';
  ctx.fillText(copy, 16, 50);
  ctx.fillText(copy, 16 + ctx.measureText(copy).width, 50);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 8;
  return tex;
}

/** High-contrast crowd for the tunnel mouth — readable from 8–20m. */
export function createMouthCrowdTexture(seed = 3) {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#141c2c';
  ctx.fillRect(0, 0, c.width, c.height);

  let rng = seed * 99991;
  const rnd = () => {
    rng = (rng * 16807) % 2147483647;
    return (rng - 1) / 2147483646;
  };

  const rows = 14;
  const rowH = (c.height - 28) / rows;
  for (let row = 0; row < rows; row++) {
    const y = 16 + row * rowH;
    const depth = 0.7 + (row / rows) * 0.3;
    ctx.fillStyle = row % 2 === 0 ? '#1b2438' : '#151c2c';
    ctx.fillRect(0, y, c.width, rowH - 2);
    ctx.fillStyle = '#0c121c';
    ctx.fillRect(0, y + rowH - 3, c.width, 2);

    for (let x = 6; x < c.width - 6; ) {
      if (Math.floor(x / 70) % 8 === 0) {
        ctx.fillStyle = '#0a0e16';
        ctx.fillRect(x, y, 12, rowH - 2);
        ctx.fillStyle = 'rgba(160,180,210,0.35)';
        ctx.fillRect(x + 5, y + 4, 2, rowH - 8);
        x += 16;
        continue;
      }
      const roll = rnd();
      if (roll < 0.1) {
        x += 5 + rnd() * 4;
        continue;
      }

      let r: number;
      let g: number;
      let b: number;
      if (roll > 0.93) {
        r = 255;
        g = 250;
        b = 230;
      } else if (roll > 0.82) {
        r = 40;
        g = 190;
        b = 255;
      } else if (roll > 0.7) {
        r = 230;
        g = 210;
        b = 70;
      } else if (roll > 0.5) {
        r = 50 + rnd() * 40;
        g = 80 + rnd() * 50;
        b = 160 + rnd() * 70;
      } else if (roll > 0.32) {
        r = 200;
        g = 70;
        b = 55;
      } else {
        r = 70 + rnd() * 50;
        g = 85 + rnd() * 45;
        b = 100 + rnd() * 40;
      }

      const bw = 3.2 + rnd() * 2.4;
      const bh = rowH * (0.55 + rnd() * 0.28);
      const bx = x;
      const by = y + rowH - bh - 3;
      ctx.globalAlpha = 0.75 + 0.25 * depth;
      ctx.fillStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
      ctx.beginPath();
      ctx.ellipse(bx + bw * 0.5, by + bh * 0.62, bw * 0.55, bh * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(bx + bw * 0.5, by + bh * 0.18, 1.3 + rnd(), 1.4 + rnd(), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      x += 4.2 + rnd() * 2.2;
    }
  }

  const vig = ctx.createLinearGradient(0, 0, 0, c.height);
  vig.addColorStop(0, 'rgba(8,14,28,0.35)');
  vig.addColorStop(0.55, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.15)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, c.width, c.height);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

/** One cheap stadium glimpse for the mouth — no heavy crowd canvases. */
export function createTunnelExitVistaTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 320;
  const ctx = c.getContext('2d')!;

  const sky = ctx.createLinearGradient(0, 0, 0, 320);
  sky.addColorStop(0, '#1a2a44');
  sky.addColorStop(0.42, '#2a3e5c');
  sky.addColorStop(0.62, '#3d4a3a');
  sky.addColorStop(1, '#2f8a48');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, 512, 320);

  ctx.fillStyle = '#1e2838';
  ctx.beginPath();
  ctx.moveTo(20, 168);
  ctx.lineTo(80, 92);
  ctx.lineTo(432, 92);
  ctx.lineTo(492, 168);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#2a3448';
  for (let r = 0; r < 7; r++) {
    ctx.fillRect(70, 98 + r * 9, 372, 7);
  }
  ctx.fillStyle = 'rgba(220,200,160,0.18)';
  for (let i = 0; i < 40; i++) {
    ctx.fillRect(90 + (i % 20) * 16, 104 + Math.floor(i / 20) * 22, 3, 4);
  }

  ctx.fillStyle = '#2aa34a';
  ctx.fillRect(48, 176, 416, 130);
  ctx.fillStyle = '#249640';
  for (let i = 0; i < 8; i++) {
    ctx.fillRect(48 + i * 52, 176, 26, 130);
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.45)';
  ctx.lineWidth = 2;
  ctx.strokeRect(64, 188, 384, 108);
  ctx.beginPath();
  ctx.moveTo(256, 188);
  ctx.lineTo(256, 296);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(256, 242, 28, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = 'rgba(255,236,190,0.35)';
  ctx.fillRect(118, 86, 8, 6);
  ctx.fillRect(248, 86, 8, 6);
  ctx.fillRect(386, 86, 8, 6);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

/** Worn stadium-tunnel concrete — panels, grout, scuffs. */
export function createTunnelConcreteTexture(kind: 'wall' | 'floor' = 'wall') {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 1024;
  const ctx = c.getContext('2d')!;
  const base = kind === 'floor' ? [78, 84, 94] : [52, 56, 64];
  ctx.fillStyle = `rgb(${base[0]},${base[1]},${base[2]})`;
  ctx.fillRect(0, 0, 1024, 1024);

  for (let i = 0; i < 18000; i++) {
    const x = Math.random() * 1024;
    const y = Math.random() * 1024;
    const n = (Math.random() - 0.5) * 22;
    ctx.fillStyle = `rgba(${base[0] + n},${base[1] + n},${base[2] + n},${0.12 + Math.random() * 0.2})`;
    ctx.fillRect(x, y, 1 + Math.random() * 2, 1 + Math.random() * 2);
  }

  ctx.strokeStyle = 'rgba(18,20,24,0.55)';
  ctx.lineWidth = 3;
  if (kind === 'wall') {
    for (let x = 0; x <= 1024; x += 256) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 1024);
      ctx.stroke();
    }
    for (let y = 0; y <= 1024; y += 341) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(1024, y);
      ctx.stroke();
    }
  } else {
    ctx.strokeStyle = 'rgba(12,14,18,0.78)';
    ctx.lineWidth = 5;
    for (let z = 0; z <= 1024; z += 128) {
      ctx.beginPath();
      ctx.moveTo(0, z);
      ctx.lineTo(1024, z);
      ctx.stroke();
    }
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(342, 0);
    ctx.lineTo(342, 1024);
    ctx.moveTo(682, 0);
    ctx.lineTo(682, 1024);
    ctx.stroke();

    ctx.strokeStyle = 'rgba(210, 218, 230, 0.58)';
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.moveTo(28, 0);
    ctx.lineTo(28, 1024);
    ctx.moveTo(996, 0);
    ctx.lineTo(996, 1024);
    ctx.stroke();

    ctx.fillStyle = 'rgba(236, 230, 210, 0.82)';
    for (let z = 24; z < 1024; z += 96) {
      ctx.fillRect(500, z, 24, 52);
    }
  }

  for (let i = 0; i < 14; i++) {
    const x = Math.random() * 512;
    const y = Math.random() * 1024;
    const stain = ctx.createRadialGradient(x, y, 0, x, y, 40 + Math.random() * 80);
    stain.addColorStop(0, 'rgba(0,0,0,0.18)');
    stain.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = stain;
    ctx.fillRect(x - 90, y - 90, 180, 180);
    ctx.fillRect(1024 - x - 90, y - 90, 180, 180);
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

/** Horizontal electric-blue anamorphic lens flare beam across the stadium portal. */
export function createAnamorphicFlareTexture() {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, c.width, c.height);

  const cy = 128;
  const cx = 512;

  // Broad horizontal flare streak (cyan-blue)
  const streak = ctx.createLinearGradient(0, cy, 1024, cy);
  streak.addColorStop(0, 'rgba(0, 150, 255, 0)');
  streak.addColorStop(0.15, 'rgba(0, 180, 255, 0.15)');
  streak.addColorStop(0.35, 'rgba(64, 210, 255, 0.6)');
  streak.addColorStop(0.48, 'rgba(180, 245, 255, 0.95)');
  streak.addColorStop(0.5, 'rgba(255, 255, 255, 1)');
  streak.addColorStop(0.52, 'rgba(180, 245, 255, 0.95)');
  streak.addColorStop(0.65, 'rgba(64, 210, 255, 0.6)');
  streak.addColorStop(0.85, 'rgba(0, 180, 255, 0.15)');
  streak.addColorStop(1, 'rgba(0, 150, 255, 0)');

  ctx.fillStyle = streak;
  ctx.fillRect(0, cy - 6, 1024, 12);

  // Thinner super-intense core laser streak
  const coreStreak = ctx.createLinearGradient(128, cy, 896, cy);
  coreStreak.addColorStop(0, 'rgba(100, 220, 255, 0)');
  coreStreak.addColorStop(0.4, 'rgba(210, 250, 255, 0.85)');
  coreStreak.addColorStop(0.5, 'rgba(255, 255, 255, 1)');
  coreStreak.addColorStop(0.6, 'rgba(210, 250, 255, 0.85)');
  coreStreak.addColorStop(1, 'rgba(100, 220, 255, 0)');
  ctx.fillStyle = coreStreak;
  ctx.fillRect(128, cy - 2, 768, 4);

  // Central bright radial starburst / bloom
  const bloom = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120);
  bloom.addColorStop(0, 'rgba(255, 255, 255, 1)');
  bloom.addColorStop(0.18, 'rgba(190, 240, 255, 0.8)');
  bloom.addColorStop(0.45, 'rgba(40, 170, 255, 0.35)');
  bloom.addColorStop(0.75, 'rgba(0, 110, 255, 0.12)');
  bloom.addColorStop(1, 'rgba(0, 70, 220, 0)');
  ctx.fillStyle = bloom;
  ctx.beginPath();
  ctx.arc(cx, cy, 120, 0, Math.PI * 2);
  ctx.fill();

  // Subtle vertical light beam spill
  const vertBeam = ctx.createLinearGradient(cx, 0, cx, 256);
  vertBeam.addColorStop(0, 'rgba(120, 210, 255, 0)');
  vertBeam.addColorStop(0.5, 'rgba(220, 245, 255, 0.35)');
  vertBeam.addColorStop(1, 'rgba(120, 210, 255, 0)');
  ctx.fillStyle = vertBeam;
  ctx.fillRect(cx - 30, 0, 60, 256);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  return tex;
}

/** Ceiling rectangular recessed light fixture texture. */
export function createCeilingLightFixtureTexture() {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 512;
  const ctx = c.getContext('2d')!;

  // Dark brushed metal housing frame
  ctx.fillStyle = '#1e2229';
  ctx.fillRect(0, 0, 256, 512);

  // Inner beveled bezel
  ctx.fillStyle = '#2d333e';
  ctx.fillRect(12, 12, 232, 488);

  // Glowing frosted diffuser pane
  const grad = ctx.createRadialGradient(128, 256, 10, 128, 256, 220);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.35, '#f4f8ff');
  grad.addColorStop(0.7, '#d8e6f8');
  grad.addColorStop(1, '#b0c8e6');
  ctx.fillStyle = grad;
  ctx.fillRect(24, 24, 208, 464);

  // Subtle interior grid lines
  ctx.strokeStyle = 'rgba(140, 170, 210, 0.25)';
  ctx.lineWidth = 2;
  for (let y = 60; y < 480; y += 40) {
    ctx.beginPath();
    ctx.moveTo(24, y);
    ctx.lineTo(232, y);
    ctx.stroke();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Textured concrete floor / ramp leading out to the pitch. */
export function createTunnelRampTexture() {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 1024;
  const ctx = c.getContext('2d')!;

  // High-density asphalt / fine textured concrete base
  ctx.fillStyle = '#4c525c';
  ctx.fillRect(0, 0, 1024, 1024);

  for (let i = 0; i < 40000; i++) {
    const x = Math.random() * 1024;
    const y = Math.random() * 1024;
    const val = 60 + (Math.random() - 0.5) * 45;
    const alpha = 0.08 + Math.random() * 0.16;
    ctx.fillStyle = `rgba(${val | 0}, ${val | 0}, ${(val + 6) | 0}, ${alpha})`;
    ctx.fillRect(x, y, 1.2, 1.2);
  }

  // Soft forward ramp highlight towards the stadium
  const glow = ctx.createLinearGradient(0, 1024, 0, 0);
  glow.addColorStop(0, 'rgba(0, 0, 0, 0.18)');
  glow.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
  glow.addColorStop(1, 'rgba(120, 190, 255, 0.22)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 1024, 1024);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

/** Left-wall manifesto from the locked exit plate. */
export function createExitManifestoTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 1024;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, 512, 1024);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(242, 213, 107, 0.45)';
  ctx.shadowBlur = 18;
  ctx.fillStyle = '#f6f1e4';
  ctx.font = '800 92px "Barlow Condensed", Impact, sans-serif';
  const lines = ['CODE.', 'BUILD.', 'DEPLOY.', 'INSPIRE.'];
  lines.forEach((line, i) => ctx.fillText(line, 256, 150 + i * 150));
  ctx.font = '800 120px "Barlow Condensed", Impact, sans-serif';
  ctx.fillStyle = '#f2d56b';
  ctx.fillText('</>', 256, 820);
  const out = new THREE.CanvasTexture(c);
  out.colorSpace = THREE.SRGBColorSpace;
  out.anisotropy = 8;
  return out;
}

/** Right-wall skill stack under the AD 12 badge. */
export function createExitSkillStackTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 1024;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, 512, 1024);
  const gold = '#e8c14a';
  const items: { label: string; draw: (x: number, y: number) => void }[] = [
    {
      label: 'DEVELOPMENT',
      draw: (x, y) => {
        ctx.font = '800 42px "Barlow Condensed", Impact, sans-serif';
        ctx.fillText('</>', x, y);
      },
    },
    {
      label: 'AI / ML',
      draw: (x, y) => {
        ctx.beginPath();
        ctx.ellipse(x, y - 4, 28, 34, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x - 10, y - 10, 5, 0, Math.PI * 2);
        ctx.arc(x + 10, y - 10, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y + 8, 12, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();
      },
    },
    {
      label: 'FULL STACK',
      draw: (x, y) => {
        for (let i = 0; i < 3; i++) {
          ctx.strokeRect(x - 28, y - 22 + i * 16, 56, 12);
        }
      },
    },
    {
      label: 'CLOUD',
      draw: (x, y) => {
        ctx.beginPath();
        ctx.arc(x - 16, y + 4, 16, 0, Math.PI * 2);
        ctx.arc(x + 6, y - 4, 20, 0, Math.PI * 2);
        ctx.arc(x + 22, y + 6, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(x - 4, y + 18, 8, 16);
        ctx.beginPath();
        ctx.moveTo(x, y + 40);
        ctx.lineTo(x - 10, y + 28);
        ctx.lineTo(x + 10, y + 28);
        ctx.closePath();
        ctx.fill();
      },
    },
  ];

  ctx.strokeStyle = gold;
  ctx.fillStyle = gold;
  ctx.lineWidth = 4;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  items.forEach((item, i) => {
    const y = 90 + i * 230;
    item.draw(88, y);
    ctx.font = '800 36px "Barlow Condensed", Impact, sans-serif';
    ctx.fillStyle = gold;
    ctx.shadowColor = 'rgba(232, 193, 74, 0.4)';
    ctx.shadowBlur = 10;
    ctx.fillText(item.label, 150, y);
  });

  const out = new THREE.CanvasTexture(c);
  out.colorSpace = THREE.SRGBColorSpace;
  out.anisotropy = 8;
  return out;
}

/** Night bowl as packed phone-flash bokeh, not readable seats. */
export function createNightBowlTexture(seed = 7) {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  const bg = ctx.createLinearGradient(0, 0, 0, 512);
  bg.addColorStop(0, '#0a1018');
  bg.addColorStop(1, '#151c28');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 1024, 512);

  let rng = seed * 99991;
  const rnd = () => {
    rng = (rng * 16807) % 2147483647;
    return (rng - 1) / 2147483646;
  };

  for (let i = 0; i < 4200; i++) {
    const x = rnd() * 1024;
    const y = rnd() * 512;
    const flash = rnd();
    const r = 0.7 + rnd() * 1.6;
    const a = 0.18 + rnd() * 0.35;
    if (flash > 0.93) {
      ctx.fillStyle = `rgba(255,252,240,${0.75 + rnd() * 0.25})`;
      ctx.beginPath();
      ctx.arc(x, y, r * 1.8, 0, Math.PI * 2);
      ctx.fill();
    } else if (flash > 0.7) {
      ctx.fillStyle = `rgba(255, 214, 120, ${a})`;
      ctx.fillRect(x, y, r, r);
    } else {
      ctx.fillStyle = `rgba(${40 + rnd() * 40},${50 + rnd() * 40},${70 + rnd() * 50},${a})`;
      ctx.fillRect(x, y, r, r);
    }
  }

  const out = new THREE.CanvasTexture(c);
  out.colorSpace = THREE.SRGBColorSpace;
  out.wrapS = THREE.RepeatWrapping;
  out.anisotropy = 8;
  return out;
}

